<template>
  <div class="scanner-wrapper">
    <van-nav-bar
      title="Scan Barcode"
      left-text="Back"
      left-arrow
      @click-left="stopAndClose"
    />

    <div id="reader" class="reader" v-show="isScanning"></div>
    
    <!-- Fallback / Error Display -->
    <div class="status-container" v-if="!isScanning">
      <div class="error-text" v-if="errorMessage">
        <van-icon name="warning-o" size="40" />
        <p>{{ errorMessage }}</p>
        <p class="sub-text" v-if="isInsecureContext">
          Browser restrictions prevent camera access on non-secure (HTTP) connections. 
          Please use HTTPS or upload an image below.
        </p>
      </div>
      <div class="loading-text" v-else>
        <van-loading type="spinner" color="#1989fa" />
        <p>Starting Camera...</p>
      </div>
    </div>

    <!-- Overlay for active scanning -->
    <div class="overlay-text" v-if="isScanning">Align code within the frame</div>

    <!-- Zoom Control -->
    <div class="zoom-control" v-if="isScanning && hasZoom">
      <div class="zoom-icon"><van-icon name="minus" size="20" /></div>
      <van-slider 
        v-model="zoomValue" 
        :min="zoomMin" 
        :max="zoomMax" 
        :step="zoomStep" 
        active-color="#1989fa"
        @input="onZoomChange"
        style="flex: 1; margin: 0 10px;"
      >
        <template #button>
           <div class="custom-button">{{ zoomValue }}x</div>
        </template>
      </van-slider>
      <div class="zoom-icon"><van-icon name="plus" size="20" /></div>
    </div>

    <!-- Controls -->
    <!-- <div class="controls-area">
      <van-uploader :after-read="handleFileUpload" accept="image/*">
        <van-button icon="photo" type="info" round block>
          Scan from Image / Photo
        </van-button>
      </van-uploader>
    </div> -->
    <div style="height: 23vh;">
      <img v-if="scanType === 'activity'" style="width: 100%; height: 100%;" src="https://voopoo-test.oss-cn-shenzhen.aliyuncs.com/iccpp-ars/status/69.jpg" alt="">
      <img v-else-if="scanType === 'activityProduct'" style="width: 100%; height: 100%;" src="https://voopoo-test.oss-cn-shenzhen.aliyuncs.com/iccpp-ars/status/SN.jpg" alt="">
    </div>
  </div>
</template>

<script>
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { Toast } from 'vant';

export default {
  name: 'BarcodeScanner',
  props: {
    scanType: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      html5QrCode: null,
      isScanning: false,
      errorMessage: '',
      isInsecureContext: false,
      // Zoom controls
      hasZoom: false,
      zoomValue: 1,
      zoomMin: 1,
      zoomMax: 5,
      zoomStep: 0.1,
      availableCameras: [],
      selectedCameraId: null
    };
  },
  mounted() {
    // Check for secure context
    if (window.isSecureContext === false) {
      this.isInsecureContext = true;
    }
    
    // Delay start to allow popup animation to complete
    setTimeout(() => {
      this.startScanner();
    }, 500);
  },
  beforeDestroy() {
    this.stopScanner();
  },
  methods: {
    async pickCameraId() {
      try {
        const preferred = window.localStorage.getItem('preferredCameraId');
        const cams = await Html5Qrcode.getCameras();
        this.availableCameras = Array.isArray(cams) ? cams : [];
        if (preferred && this.availableCameras.find(c => c.id === preferred)) {
          this.selectedCameraId = preferred;
          return;
        }
        if (this.availableCameras.length === 0) {
          this.selectedCameraId = null;
          return;
        }
        const preferPatterns = [/back/i, /rear/i, /wide/i, /main/i, /primary/i, /camera\s?0/i];
        const avoidPatterns = [/tele/i, /zoom/i, /macro/i, /bokeh/i, /depth/i, /portrait/i];
        const scored = this.availableCameras.map((c, idx) => {
          const label = c.label || '';
          let score = 0;
          preferPatterns.forEach(p => { if (p.test(label)) score += 2; });
          avoidPatterns.forEach(p => { if (p.test(label)) score -= 3; });
          if (/front/i.test(label)) score -= 5;
          return { c, score, idx };
        }).sort((a, b) => b.score - a.score || a.idx - b.idx);
        this.selectedCameraId = scored[0].c.id;
        window.localStorage.setItem('preferredCameraId', this.selectedCameraId);
      } catch (e) {
        this.selectedCameraId = null;
      }
    },
    async startScanner() {
      this.errorMessage = '';
      try {
        const element = document.getElementById('reader');
        if (!element) {
          throw new Error('Scanner element not found');
        }

        // Initialize if not already done
        if (!this.html5QrCode) {
          this.html5QrCode = new Html5Qrcode("reader");
        }

        await this.pickCameraId();

        const config = {
          fps: 20, // Optimized for smoother scanning
          qrbox: { width: 250, height: 250 },
          experimentalFeatures: {
            useBarCodeDetectorIfSupported: true
          },
          rememberLastUsedCamera: false,
          aspectRatio: 1.7777778,
          formatsToSupport: [
             Html5QrcodeSupportedFormats.QR_CODE,
             Html5QrcodeSupportedFormats.EAN_13,
             Html5QrcodeSupportedFormats.EAN_8,
             Html5QrcodeSupportedFormats.CODE_128,
             Html5QrcodeSupportedFormats.UPC_A,
             Html5QrcodeSupportedFormats.UPC_E,
             Html5QrcodeSupportedFormats.CODE_39
          ],
          videoConstraints: {
            width: { ideal: 1920 }, // Higher res for better digital zoom
            height: { ideal: 1080 },
            facingMode: 'environment',
            focusMode: 'continuous', // Try to force continuous focus
            advanced: [{ focusMode: 'continuous' }, { zoom: 1.0 }]
          }
        };

        const cameraParam = this.selectedCameraId ? this.selectedCameraId : { facingMode: 'environment' };
        await this.html5QrCode.start(
          cameraParam,
          config,
          (decodedText, decodedResult) => {
            this.onScanSuccess(decodedText);
          },
          (errorMessage) => {
            // Ignore scan errors
          }
        );
        
        this.isScanning = true;
        
        // Initialize Zoom Capabilities
        setTimeout(() => {
          try {
            const capabilities = this.html5QrCode.getRunningTrackCameraCapabilities();
            if (capabilities && capabilities.zoom) {
               this.hasZoom = true;
               this.zoomMin = capabilities.zoom.min || 1;
               this.zoomMax = capabilities.zoom.max || 5;
               this.zoomStep = capabilities.zoom.step || 0.1;
               this.zoomValue = this.zoomMin <= 1 ? 1 : this.zoomMin;
               this.html5QrCode.applyVideoConstraints({ advanced: [{ zoom: this.zoomValue }] }).catch(() => {});
            } else {
               this.hasZoom = false;
            }
          } catch (e) {
            console.log("Could not get camera capabilities", e);
            this.hasZoom = false;
          }
        }, 500); // Small delay to ensure track is ready

      } catch (err) {
        console.error("Error starting scanner", err);
        this.isScanning = false;
        this.errorMessage = err.message || 'Camera failed to start';
        
        // Specific check for the reported error
        if (typeof err === 'string' && err.includes("Camera streaming not supported")) {
             this.errorMessage = "Camera not supported in this browser/mode.";
             this.isInsecureContext = true; // Likely the cause
        }
      }
    },
    
    async onZoomChange(value) {
      if (this.html5QrCode && this.isScanning) {
        try {
          await this.html5QrCode.applyVideoConstraints({
             advanced: [{ zoom: value }]
          });
        } catch (err) {
          console.error("Failed to apply zoom", err);
        }
      }
    },
    
    async stopScanner() {
      if (this.html5QrCode && this.isScanning) {
        try {
          await this.html5QrCode.stop();
          this.isScanning = false;
        } catch (err) {
          console.error("Failed to stop scanner", err);
        }
      }
    },

    async handleFileUpload(file) {
      if (!file || !file.file) return;
      
      Toast.loading({
        message: 'Processing...',
        forbidClick: true,
      });

      try {
        // If instance doesn't exist, create it (but don't start camera)
        if (!this.html5QrCode) {
           this.html5QrCode = new Html5Qrcode("reader");
        }

        const result = await this.html5QrCode.scanFileV2(file.file, true);
        if (result && result.decodedText) {
           Toast.success('Code Found!');
           this.onScanSuccess(result.decodedText);
        } else {
           throw new Error('No code detected');
        }
      } catch (err) {
        console.error("File scan error", err);
        Toast.fail('Could not detect barcode in image. Please try again.');
      }
    },

    onScanSuccess(code) {
      this.stopScanner();
      this.$emit('on-scan-success', code);
    },

    stopAndClose() {
      this.stopScanner();
      // If we created an instance but never started (or stopped), clear it
      if (this.html5QrCode) {
        this.html5QrCode.clear();
        this.html5QrCode = null;
      }
      this.$emit('close');
    }
  }
};
</script>

<style scoped lang="less">
.scanner-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: #222;
  z-index: 3000;
  display: flex;
  flex-direction: column;
  align-items: center;

  .reader {
    width: 100%;
    height: 60vh;
    background: #000;
    position: relative;
    
    /deep/ video {
      width: 100% !important;
      height: 100% !important;
      object-fit: cover !important;
    }
  }

  .status-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
    text-align: center;
    color: #fff;
    width: 100%;

    .error-text {
      color: #ff6b6b;
      .sub-text {
        color: #ccc;
        font-size: 14px;
        margin-top: 10px;
      }
    }

    .loading-text {
      color: #fff;
    }
  }

  .overlay-text {
    position: absolute;
    top: 100px;
    width: 100%;
    text-align: center;
    color: white;
    font-size: 16px;
    text-shadow: 1px 1px 3px rgba(0,0,0,0.8);
    z-index: 3001;
    pointer-events: none;
  }

  .zoom-control {
    position: absolute;
    bottom: 120px; /* Above controls area if any */
    width: 80%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.5);
    padding: 10px 20px;
    border-radius: 20px;
    z-index: 3002;

    .zoom-icon {
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .custom-button {
      min-width: 30px;
      padding: 0 5px;
      color: #fff;
      font-size: 10px;
      line-height: 18px;
      text-align: center;
      background-color: #1989fa;
      border-radius: 100px;
    }
  }

  .controls-area {
    position: absolute;
    bottom: 50px;
    width: 80%;
    z-index: 3002;
  }

  .close-icon {
    position: absolute;
    top: 20px;
    right: 20px;
    font-size: 30px;
    color: #fff;
    z-index: 3002;
    cursor: pointer;
    padding: 10px;
    background: rgba(0,0,0,0.3);
    border-radius: 50%;
  }

  // Deep selector to style the child component van-nav-bar
  /deep/ .van-nav-bar {
    width: 100%;
    background-color: #ee541b; // Match the wrapper background
  }
  /deep/ .van-nav-bar__title,
  /deep/ .van-nav-bar__text,
  /deep/ .van-nav-bar .van-icon {
    color: #fff !important; // Style the title and icons
  }
}
</style>
